// User profile service for storing user preferences in Firestore

import { getDocumentByUserId, updateDocument, setDocumentWithId } from './firebase/firestore.service';
import type { FirestoreDocument } from './firebase/firestore.service';
import type { UnitSystem } from '../utils/unitConversions';

const USER_PROFILES_COLLECTION = 'userProfiles';

export type DoseDay = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

// ITEM 5: Unit system split - body units vs food units
export interface UnitPreferences {
  body: UnitSystem;
  food: UnitSystem;
}

export interface UserProfile {
  userId: string;
  // ITEM 5: Legacy field - kept for migration, deprecated
  unitSystem?: UnitSystem;
  // ITEM 5: New structure - body and food units are independent
  units?: UnitPreferences;
  glp1Enabled?: boolean;
  humourEnabled?: boolean;
  glp1DoseDay?: DoseDay | null; // SPRINT A: GLP-1 dose day
  glp1DoseDayNudgeEnabled?: boolean; // SPRINT A: Dose-day nudge toggle (default: true)
  notifications_enabled?: boolean; // SPRINT B: Global notifications toggle (default: true)
  injection_marked_done?: boolean; // SPRINT B: GLP-1 injection marked done (default: false)
  glp1_hub_intro_acknowledged?: boolean; // Phase 1: GLP-1 Hub intro panel acknowledgment
  stravaImportEnabled?: boolean; // Sprint D1: Strava import toggle (default: true when connected)
  onboarding_complete?: boolean; // Phase 1: Onboarding flow completion
  primaryGoal?: 'lose_weight' | 'maintain_weight' | 'gain_muscle'; // Phase 1: Primary goal from onboarding
  dateOfBirth?: string; // YYYY-MM-DD
  gender?: 'male' | 'female' | 'other';
  isAdmin?: boolean; // Admin access flag - only true for owner/admin account
  // Sprint D1: First-use tip bubble flags (one-time only)
  seen_nutrition_tip?: boolean;
  seen_exercise_tip?: boolean;
  seen_strava_tip?: boolean;
  seen_offline_tip?: boolean;
  seen_body_stats_tip?: boolean;
  // ITEM 3: Add-to-Home-Screen guidance (one-time only)
  hasSeenA2HS?: boolean;
  // First name / preferred name — captured at registration, used for personalisation
  firstName?: string;
  // Celebration system: starting weight captured at onboarding — written once, never updated
  baselineWeight?: number; // kg
  // First-session insight flags — one per log type, set once permanently
  firstInsightShown?: {
    food?: boolean;
    workout?: boolean;
    body?: boolean;
  };
  // Functional Age first-run gate — set to true after first successful FA compute
  functionalAgeHasEverComputed?: boolean;
  // Starter modals — one-time per screen, Firestore-persisted so they survive logout/reinstall
  starter_modals_seen?: Partial<Record<'home' | 'nutrition' | 'exercise' | 'body_stats' | 'functional_age' | 'glp1_hub' | 'a2hs', boolean>>;
  // Premium Preview deferred activation — set when user taps preview before email is verified
  premiumPreviewPending?: boolean;
  // Durable verification state — written once when user completes email verification
  emailVerificationCompletedAt?: number;
  createdAt?: number;
  updatedAt?: number;
}

/**
 * Get user profile
 */
export async function getUserProfile(userId: string): Promise<FirestoreDocument<UserProfile> | null> {
  return getDocumentByUserId<UserProfile>(USER_PROFILES_COLLECTION, userId);
}

/**
 * Save or update user profile
 */
export async function saveUserProfile(
  userId: string,
  updates: Partial<Omit<UserProfile, 'userId' | 'createdAt' | 'updatedAt'>>,
): Promise<FirestoreDocument<UserProfile>> {
  const existing = await getUserProfile(userId);
  const now = Date.now();
  
  if (existing) {
    // Update existing profile
    // Use the doc path based on actual doc ID (could be UID or legacy auto-ID)
    await updateDocument(
      `${USER_PROFILES_COLLECTION}/${existing.id}`,
      {
        ...updates,
        updatedAt: now,
      },
    );
    return {
      ...existing,
      ...updates,
      updatedAt: now,
    };
  } else {
    // Create new profile — keyed by Firebase Auth UID
    const newProfile: Omit<UserProfile, 'id'> = {
      userId,
      ...updates,
      createdAt: now,
      updatedAt: now,
    };
    return setDocumentWithId(USER_PROFILES_COLLECTION, userId, newProfile, { merge: true });
  }
}

/**
 * ITEM 5: Auto-detect country from browser locale
 * Returns both body and food units based on location
 * US → both imperial, UK/others → both metric
 */
function detectDefaultUnits(): UnitPreferences {
  try {
    // Check navigator.language or navigator.languages
    const languages = navigator.languages || [navigator.language];
    
    for (const lang of languages) {
      const lowerLang = lang.toLowerCase();
      // Check if locale contains en-US or ends with -US
      if (lowerLang.includes('en-us') || lowerLang.endsWith('-us')) {
        // US → both imperial
        return { body: 'imperial', food: 'imperial' };
      }
      // Check for UK (en-GB)
      if (lowerLang.includes('en-gb') || lowerLang.endsWith('-gb')) {
        // UK → both metric
        return { body: 'metric', food: 'metric' };
      }
    }
    
    // Check primary language
    const primaryLang = navigator.language?.toLowerCase() || '';
    if (primaryLang.includes('en-us') || primaryLang.endsWith('-us')) {
      return { body: 'imperial', food: 'imperial' };
    }
    if (primaryLang.includes('en-gb') || primaryLang.endsWith('-gb')) {
      return { body: 'metric', food: 'metric' };
    }
  } catch (error) {
    console.warn('Failed to detect locale for unit system:', error);
  }
  
  // Default to metric for all other countries
  return { body: 'metric', food: 'metric' };
}

/**
 * Legacy function - kept for backward compatibility
 * Auto-detect country from browser locale
 * Returns 'imperial' for US, 'metric' for all other countries
 * NOTE: Currently unused but kept for potential future use
 * @internal
 */
// @ts-expect-error TS6133 - Function is kept for potential future use
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function _detectDefaultUnitSystem(): UnitSystem {
  return detectDefaultUnits().body;
}

/**
 * ITEM 5: Migrate legacy unitSystem to new units structure
 * This runs silently on first load (not gated on Settings visit)
 */
async function migrateUnitSystem(userId: string, profile: FirestoreDocument<UserProfile> | null): Promise<UnitPreferences> {
  // If already migrated, return existing units
  if (profile?.units) {
    return profile.units;
  }
  
  // If legacy unitSystem exists, migrate it
  if (profile?.unitSystem) {
    const migrated: UnitPreferences = {
      body: profile.unitSystem,
      food: profile.unitSystem, // Initially both use the same value
    };
    
    // Save migrated structure (silently, don't wait)
    saveUserProfile(userId, { units: migrated }).catch(error => {
      console.error('[UserProfile] Failed to save migrated units:', error);
    });
    
    console.log('[UserProfile] ITEM 5: Migrated legacy unitSystem to units structure:', {
      userId,
      legacy: profile.unitSystem,
      migrated,
    });
    
    return migrated;
  }
  
  // No existing preference - auto-detect
  const defaultUnits = detectDefaultUnits();
  
  // Save auto-detected defaults (silently, don't wait)
  saveUserProfile(userId, { units: defaultUnits }).catch(error => {
    console.error('[UserProfile] Failed to save auto-detected units:', error);
  });
  
  console.log('[UserProfile] ITEM 5: Auto-detected units:', {
    userId,
    detectedUnits: defaultUnits,
    languages: navigator.languages || [navigator.language],
  });
  
  return defaultUnits;
}

/**
 * ITEM 5: Get unit preferences (body and food units)
 * Handles migration from legacy unitSystem automatically
 */
export async function getUnitPreferences(userId: string): Promise<UnitPreferences> {
  // ITEM 5: Check localStorage cache first for speed
  try {
    const cached = localStorage.getItem('coretrak.settings.units');
    if (cached) {
      const parsed = JSON.parse(cached) as UnitPreferences;
      // Validate structure
      if (parsed.body && parsed.food && (parsed.body === 'metric' || parsed.body === 'imperial') && (parsed.food === 'metric' || parsed.food === 'imperial')) {
        // Cache is valid, but still migrate in background if needed
        const profile = await getUserProfile(userId);
        migrateUnitSystem(userId, profile).catch(error => {
          console.error('[UserProfile] Background migration failed:', error);
        });
        return parsed;
      }
    }
  } catch (error) {
    console.error('Failed to read unit preferences from localStorage:', error);
  }
  
  const profile = await getUserProfile(userId);
  
  // Migrate if needed (silent migration on first load)
  const units = await migrateUnitSystem(userId, profile);
  
  // Cache in localStorage for speed
  try {
    localStorage.setItem('coretrak.settings.units', JSON.stringify(units));
  } catch (error) {
    console.error('Failed to cache unit preferences:', error);
  }
  
  return units;
}

/**
 * Legacy function - kept for backward compatibility
 * Returns body units (for components that haven't been migrated yet)
 * BUG FIX D1: Auto-detect country on first use (US → Imperial, others → Metric)
 */
export async function getUnitSystem(userId: string): Promise<UnitSystem> {
  const units = await getUnitPreferences(userId);
  return units.body;
}

/**
 * Update user profile (alias for saveUserProfile for clarity)
 */
export async function updateUserProfile(
  userId: string,
  updates: Partial<Omit<UserProfile, 'userId' | 'createdAt' | 'updatedAt'>>,
): Promise<FirestoreDocument<UserProfile>> {
  return saveUserProfile(userId, updates);
}

/**
 * ITEM 5: Save unit preferences (body and food units)
 */
export async function saveUnitPreferences(userId: string, units: UnitPreferences): Promise<void> {
  // Save to Firestore
  await saveUserProfile(userId, { units });
  
  // Cache in localStorage for speed
  try {
    localStorage.setItem('coretrak.settings.units', JSON.stringify(units));
  } catch (error) {
    console.error('Failed to cache unit preferences:', error);
  }
}

/**
 * Legacy function - kept for backward compatibility
 * Saves to both body and food units (for components that haven't been migrated yet)
 */
export async function saveUnitSystem(userId: string, unitSystem: UnitSystem): Promise<void> {
  // When legacy function is called, update both body and food
  const currentUnits = await getUnitPreferences(userId);
  await saveUnitPreferences(userId, {
    body: unitSystem,
    food: currentUnits.food, // Preserve food preference
  });
  
  // Also save legacy field for backward compatibility
  await saveUserProfile(userId, { unitSystem });
  
  // Mirror to localStorage for speed
  try {
    localStorage.setItem('coretrak.settings.unitSystem', unitSystem);
  } catch (error) {
    console.error('Failed to cache unit system:', error);
  }
}

/**
 * Check if user is admin
 * Server-side check: Fetches user profile from Firestore and checks isAdmin field
 * Also checks allowlist of admin emails/userIds as fallback
 * 
 * @param userId - User ID to check
 * @returns true if user is admin, false otherwise
 */
export async function isUserAdmin(userId: string): Promise<boolean> {
  try {
    // Admin allowlist (fallback for MVP/Beta) - reserved for future use
    // To use allowlist, uncomment and add admin user IDs or emails:
    // const ADMIN_ALLOWLIST: string[] = [
    //   // Add Nick's user ID or email here when known
    //   // Example: 'nick@example.com' or 'user-id-here'
    // ];
    
    // Fetch user profile and check isAdmin field
    const profile = await getUserProfile(userId);
    if (profile?.isAdmin === true) {
      return true;
    }

    // Note: Allowlist check can be added here in the future if needed
    // This would require getting user email from auth context
    
    return false;
  } catch (error) {
    console.error('Failed to check admin status:', error);
    // Fail secure: deny access on error
    return false;
  }
}


/**
 * getUserProfileResult
 *
 * Structured profile load — returns explicit found / missing / error states.
 * AppContext uses this instead of getUserProfile() so it never has to infer
 * new-user state from catch blocks or in-memory flags.
 *
 * getUserProfile() is left unchanged for all other callers.
 */
export type UserProfileLoadResult =
  | { status: 'found'; profile: FirestoreDocument<UserProfile> }
  | { status: 'missing' }
  | { status: 'error'; error: unknown };

export async function getUserProfileResult(
  userId: string,
): Promise<UserProfileLoadResult> {
  try {
    const profile = await getUserProfile(userId);
    if (!profile) {
      return { status: 'missing' };
    }
    return { status: 'found', profile };
  } catch (error) {
    return { status: 'error', error };
  }
}

export type StarterModalScreen =
  | 'home'
  | 'nutrition'
  | 'exercise'
  | 'body_stats'
  | 'functional_age'
  | 'glp1_hub'
  | 'a2hs';

/**
 * markStarterModalSeen
 *
 * Writes a single starter modal seen flag using Firestore dot-notation so only
 * that one key is updated. Never overwrites other screen flags in the map.
 *
 * Do NOT use saveUserProfile({ starter_modals_seen: { [screen]: true } }) —
 * that performs a shallow update and wipes all other screen flags.
 */
export async function markStarterModalSeen(
  userId: string,
  screen: StarterModalScreen,
): Promise<void> {
  try {
    const existing = await getUserProfile(userId);
    if (!existing) return;
    await updateDocument(
      `${USER_PROFILES_COLLECTION}/${existing.id}`,
      { [`starter_modals_seen.${screen}`]: true },
    );
  } catch (err) {
    console.warn('[userProfile] markStarterModalSeen: Firestore write failed (non-fatal):', err);
  }
}
